import React, { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import { Card } from "../ui";

/**
 * Lu.ma-leaning detail hero: soft cover band + light body.
 * Title lives on the cover; meta, description, and CTA sit on white below.
 */
const DetailHeroCard = forwardRef(function DetailHeroCard({
  color,
  eyebrow = null,
  title,
  titleTestId = "detail-title",
  aside = null,
  meta = null,
  /** @deprecated Prefer `meta` — still rendered in the light body */
  chips = null,
  children = null,
  cta = null,
  contentMuted = false,
  className = "",
  testId = "detail-hero",
}, ref) {
  const bodyMeta = meta || chips;

  return (
    <section ref={ref} className={twMerge("max-w-page mx-auto px-4 pt-3", className)} data-testid={testId}>
      <div className="quiet-card overflow-hidden">
        <Card
          hero
          color={color}
          className="relative h-[9.75rem] sm:h-[11.5rem] rounded-none border-0 shadow-none"
        >
          <div
            className="detail-hero-scrim absolute inset-0 bg-gradient-to-t from-black/60 via-black/28 to-black/10 pointer-events-none"
            aria-hidden
          />
          {aside ? (
            <div className="absolute top-3.5 right-3.5 z-[1] sm:top-4 sm:right-4">{aside}</div>
          ) : null}
          <div
            className={twMerge(
              "relative h-full flex flex-col justify-end p-5 sm:p-6 pr-24 sm:pr-28",
              contentMuted && "opacity-45 transition-opacity duration-200",
            )}
          >
            {eyebrow}
            <h1
              className="main-title font-display font-medium text-[28px] sm:text-[32px] leading-[1.05] tracking-[-0.02em] text-white mt-1 text-balance break-words"
              data-testid={titleTestId}
            >
              {title}
            </h1>
          </div>
        </Card>

        <div
          className={twMerge(
            "bg-surface px-5 sm:px-6 py-5 space-y-4",
            contentMuted && "opacity-45 transition-opacity duration-200",
          )}
        >
          {bodyMeta ? <div className="space-y-2.5">{bodyMeta}</div> : null}
          {children}
          {cta ? <div className={children || bodyMeta ? "pt-1" : undefined}>{cta}</div> : null}
        </div>
      </div>
    </section>
  );
});

export default DetailHeroCard;

/** Quiet icon + text row for date / venue / links on detail pages. */
export function DetailMetaRow({
  icon: Icon,
  children,
  href = null,
  external = false,
  className = "",
  testId,
}) {
  const inner = (
    <>
      {Icon ? (
        <Icon className="w-4 h-4 shrink-0 mt-0.5 text-ink-secondary" strokeWidth={2} aria-hidden />
      ) : null}
      <span className="min-w-0 break-words">{children}</span>
    </>
  );

  const shared = twMerge(
    "inline-flex items-start gap-2.5 font-ui text-[14px] font-medium text-ink-secondary",
    className,
  );

  if (href) {
    return (
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noreferrer" : undefined}
        className={twMerge(shared, "hover:text-ink transition-colors")}
        data-testid={testId}
      >
        {inner}
      </a>
    );
  }

  return (
    <p className={shared} data-testid={testId}>
      {inner}
    </p>
  );
}
